/* =============================================================================
 * MSync Water Meter Management — data layer
 * Real structure + values lifted from the MSync.Database seed scripts:
 *   Data.Location.sql, Data.InvoiceDefinition.sql, Data.InvoiceLineDefinition.sql,
 *   Data.QBOAccount.sql, Data.MeterReadType.sql, Data.UnitType.sql
 * Meter readings, invoices and sensor telemetry are synthesised on top of the
 * real units/templates so every screen is populated and self-consistent.
 * ===========================================================================*/
(function () {
  "use strict";

  // Quiet a benign dev-only warning caused by the host editor stamping
  // data-om-id onto React.Fragment nodes (not from app code).
  const _ce = console.error;
  console.error = function () {
    const m = arguments[0];
    if (typeof m === "string" && m.indexOf("React.Fragment") !== -1) return;
    return _ce.apply(console, arguments);
  };

  // Anchor "now" to the current project date.
  const NOW = new Date(2026, 4, 31); // May 31 2026

  // ---- deterministic PRNG so data is stable across reloads -----------------
  function mulberry32(a) {
    return function () {
      a |= 0; a = (a + 0x6D2B79F5) | 0;
      let t = Math.imul(a ^ (a >>> 15), 1 | a);
      t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
      return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
  }
  function seeded(seedStr) {
    let h = 2166136261;
    for (let i = 0; i < seedStr.length; i++) { h ^= seedStr.charCodeAt(i); h = Math.imul(h, 16777619); }
    return mulberry32(h);
  }

  // ----------------------------------------------------------- reference data
  const meterReadTypes = {
    1: { id: 1, name: "Manual", desc: "Manual Read" },
    2: { id: 2, name: "Calculated", desc: "Calculated Read" },
    4: { id: 4, name: "Estimated", desc: "Estimated Read" },
    8: { id: 8, name: "Base", desc: "Base Read" },
  };
  const unitTypes = {
    1: { id: 1, name: "CF", desc: "Cubic Feet" },
    2: { id: 2, name: "HCF", desc: "Hundred Cubic Feet" },
    3: { id: 3, name: "GAL", desc: "Gallons" },
  };

  // --------------------------------------------------------- property managers
  // QBOAccount rows == the property managers / billing companies.
  const managers = [
    { id: "siligre", company: "Siligre", name: "Siligre Properties, LLC", realmId: "157146151",
      bank: "Chase \u00b7 Acct #890008063", zelle: "management@siligre.com",
      qbo: { connected: true, tokenDaysLeft: 41, lastSync: addDays(NOW, -0.18) } },
    { id: "greg", company: "GregStarobin", name: "Greg Starobin", realmId: "354886560",
      bank: "Chase \u00b7 Acct #811006910", zelle: null,
      qbo: { connected: true, tokenDaysLeft: 12, lastSync: addDays(NOW, -0.4) } },
    { id: "guy", company: "GuyColpitts", name: "Colpitts Properties", realmId: "123145756523012",
      bank: "Bank of America \u00b7 Acct #325049828892", zelle: null,
      qbo: { connected: false, tokenDaysLeft: -3, lastSync: addDays(NOW, -6.2) } },
    { id: "sharon", company: "SharonKianfar", name: "Sharon Kianfar", realmId: "9130348044635276",
      bank: "Chase \u00b7 Acct #393593923", zelle: null,
      qbo: { connected: true, tokenDaysLeft: 78, lastSync: addDays(NOW, -0.21) } },
    { id: "lily", company: "LilyStarobinets", name: "Lily Starobinets", realmId: "420896250",
      bank: "Chase \u00b7 Acct #810229513", zelle: "lily@starobinets.com",
      qbo: { connected: true, tokenDaysLeft: 55, lastSync: addDays(NOW, -0.25) } },
  ];

  // -------------------------------------------------------- invoice templates
  // InvoiceDefinition + InvoiceLineDefinition. fixed=true billed per month;
  // fixed=false is the metered volume charge ($/unit of usage).
  const templates = [
    { id: "rena", defId: "539E4B64", managerId: "siligre", name: "Rena Template",
      term: "Due on 20th", invoiceDay: 20, frequency: 1,
      memo: "Payment can also be made via Zelle to: management@siligre.com or teller deposit at any Chase branch to: Siligre Properties, LLC \u00b7 Account #890008063",
      autoPayMemo: "To be auto-debited on or after the payment due date.",
      lines: [
        { order: 10, fixed: true, desc: "Sanitary District", price: 25.48, account: "Utilities" },
        { order: 20, fixed: true, desc: "Water/Sewer Fixed Charge", price: 18.54, account: "Utilities" },
        { order: 30, fixed: false, desc: "Water/Sewer Volume Charge", price: 4.81, account: "Utilities" },
      ] },
    { id: "teakS", defId: "B72EDA3C", managerId: "siligre", name: "Teak Template \u2014 Siligre",
      term: "Due on 5th (NM)", invoiceDay: 5, frequency: 1,
      memo: "Payment can also be made via Zelle to: management@siligre.com or teller deposit at any Chase branch to: Siligre Properties, LLC \u00b7 Account #890008063",
      autoPayMemo: "To be auto-debited on or after the payment due date.",
      lines: [
        { order: 10, fixed: true, desc: "Water Monthly Flat Rate", price: 9.66, account: "Utilities" },
        { order: 20, fixed: true, desc: "Sewer Monthly Flat Rate", price: 8.20, account: "Utilities" },
        { order: 30, fixed: false, desc: "Water/Sewer Volume Charge", price: 14.76, account: "Utilities" },
      ] },
    { id: "fourth", defId: "CE00447A", managerId: "siligre", name: "4th Template \u2014 Siligre",
      term: "Due on 5th (NM)", invoiceDay: 5, frequency: 1,
      memo: "Payment can also be made via Zelle to: management@siligre.com or teller deposit at any Chase branch to: Siligre Properties, LLC \u00b7 Account #890008063",
      autoPayMemo: "To be auto-debited on or after the payment due date.",
      lines: [
        { order: 10, fixed: true, desc: "City Sewer Charge", price: 15.32, account: "Utilities" },
        { order: 20, fixed: true, desc: "Sanitation District Charge", price: 25.48, account: "Utilities" },
        { order: 30, fixed: true, desc: "Meter Charge", price: 17.13, account: "Utilities" },
        { order: 40, fixed: true, desc: "Storm Water Fee", price: 4.85, account: "Utilities" },
        { order: 50, fixed: false, desc: "Water Volume Charge", price: 5.95, account: "Utilities" },
      ] },
    { id: "cienaga", defId: "A35FC1B5", managerId: "siligre", name: "Cienaga Template \u2014 Siligre",
      term: "Due on 5th (NM)", invoiceDay: 5, frequency: 1,
      memo: "Payment can also be made via Zelle to: management@siligre.com",
      autoPayMemo: "To be auto-debited on or after the payment due date.",
      lines: [
        { order: 10, fixed: true, desc: "City Sewer Charge", price: 9.75, account: "Utilities" },
        { order: 20, fixed: true, desc: "Sanitation District Charge", price: 25.52, account: "Utilities" },
        { order: 30, fixed: true, desc: "Water Base Charge", price: 29.14, account: "Utilities" },
        { order: 50, fixed: false, desc: "Water Volume Charge", price: 4.19, account: "Utilities" },
      ] },
    { id: "teakG", defId: "E24F7B9F", managerId: "greg", name: "Teak Template \u2014 GregStarobin",
      term: "Due on 5th", invoiceDay: 5, frequency: 1,
      memo: "Payment can also be made via teller deposit at any Chase branch to account #811006910",
      autoPayMemo: "To be auto-debited on or after the payment due date.",
      lines: [
        { order: 10, fixed: true, desc: "Water Monthly Flat Rate", price: 7.25, account: "Utilities" },
        { order: 20, fixed: true, desc: "Sewer Monthly Flat Rate", price: 6.15, account: "Utilities" },
        { order: 30, fixed: false, desc: "Water/Sewer Volume Charge", price: 14.76, account: "Utilities" },
      ] },
    { id: "manhattan", defId: "AB99FD94", managerId: "guy", name: "Manhattan Template \u2014 GuyColpitts",
      term: "Due on 5th", invoiceDay: 5, frequency: 1,
      memo: "Payment can also be made via teller deposit at any Bank of America branch to: Colpitts Properties \u00b7 Account #325049828892",
      autoPayMemo: "To be auto-debited on or after the payment due date.",
      lines: [
        { order: 10, fixed: true, desc: "City Sewer Charge", price: 15.32, account: "Utilities" },
        { order: 20, fixed: true, desc: "Sanitation District Charge", price: 25.48, account: "Utilities" },
        { order: 30, fixed: true, desc: "Meter Charge", price: 17.13, account: "Utilities" },
        { order: 40, fixed: true, desc: "Storm Water Fee", price: 4.85, account: "Utilities" },
        { order: 50, fixed: false, desc: "Water Volume Charge", price: 5.95, account: "Utilities" },
      ] },
    { id: "seventh", defId: "2AF17F91", managerId: "sharon", name: "7th Template",
      term: "Due on 5th", invoiceDay: 5, frequency: 1,
      memo: "Payment can also be made via teller deposit at any Chase branch to: Sharon Kianfar \u00b7 Account #393593923",
      autoPayMemo: "To be auto-debited on or after the payment due date.",
      lines: [
        { order: 10, fixed: true, desc: "City Sewer Charge", price: 15.32, account: "Utilities" },
        { order: 20, fixed: true, desc: "Sanitation District Charge", price: 25.48, account: "Utilities" },
        { order: 30, fixed: true, desc: "Meter Charge", price: 17.13, account: "Utilities" },
        { order: 40, fixed: true, desc: "Storm Water Fee", price: 4.85, account: "Utilities" },
        { order: 50, fixed: false, desc: "Water Volume Charge", price: 5.95, account: "Utilities" },
      ] },
    { id: "spring", defId: "4F7C87E0", managerId: "lily", name: "Spring Template \u2014 LilyStarobinets",
      term: "Due on 5th", invoiceDay: 5, frequency: 1,
      memo: "Payment can also be made via Zelle to: lily@starobinets.com or teller deposit at any Chase branch to: Lily Starobinets \u00b7 Account #810229513",
      autoPayMemo: "To be auto-debited on or after the payment due date.",
      lines: [
        { order: 10, fixed: true, desc: "Water Monthly Flat Rate", price: 7.25, account: "Utilities" },
        { order: 20, fixed: true, desc: "Sewer Monthly Flat Rate", price: 6.15, account: "Utilities" },
        { order: 30, fixed: false, desc: "Water/Sewer Volume Charge", price: 14.76, account: "Utilities" },
      ] },
  ];

  // ----------------------------------------------------------------- units
  // Raw rows from Data.Location.sql (active + historical). tenantRaw is the
  // packed QBOTenantName; we parse a clean display name off it.
  // fields: managerId, templateId, propKey, propName, name, unit, tenantRaw,
  //         sensorId, counter, scale, moveIn, moveOut, autoInvoice, autoEmail,
  //         invoiceDay, minDays, autoPay, active, baseUse(avg monthly units)
  const U = (o) => o;
  const rawUnits = [
    // ---- Siligre / 216 S. Rena ----
    U({ managerId:"siligre", templateId:"rena", propKey:"rena", propName:"216 S. Rena", name:"216 S. Rena B1", unit:"B1", tenantRaw:"216 Rena:B1:Sabrina Graham/Ethan Brandle:Water (Sabrina Graham/Ethan Brandle)", sensorId:156194, counter:1, scale:.001, moveIn:"2022-10-16", moveOut:null, autoInvoice:true, autoEmail:true, invoiceDay:20, minDays:15, autoPay:false, active:true, baseUse:9 }),
    U({ managerId:"siligre", templateId:"rena", propKey:"rena", propName:"216 S. Rena", name:"216 S. Rena B2", unit:"B2", tenantRaw:"216 Rena:B2:Weiwei An:Water (Weiwei An)", sensorId:156194, counter:2, scale:.001, moveIn:"2022-06-15", moveOut:null, autoInvoice:true, autoEmail:true, invoiceDay:20, minDays:15, autoPay:false, active:true, baseUse:6 }),
    U({ managerId:"siligre", templateId:"rena", propKey:"rena", propName:"216 S. Rena", name:"216 S. Rena B3", unit:"B3", tenantRaw:"216 Rena:B3:Timothy Ganiron:Water (Timothy Ganiron)", sensorId:156194, counter:3, scale:.001, moveIn:"2022-08-01", moveOut:null, autoInvoice:true, autoEmail:true, invoiceDay:20, minDays:15, autoPay:false, active:true, baseUse:11 }),
    U({ managerId:"siligre", templateId:"rena", propKey:"rena", propName:"216 S. Rena", name:"216 S. Rena B4", unit:"B4", tenantRaw:"216 Rena:B4:Emili Reimers/Neil Rodriguez:Water (Emili Reimers/Neil Rodriguez)", sensorId:156194, counter:4, scale:.001, moveIn:"2025-07-01", moveOut:null, autoInvoice:true, autoEmail:true, invoiceDay:20, minDays:15, autoPay:false, active:true, baseUse:8 }),
    U({ managerId:"siligre", templateId:"rena", propKey:"rena", propName:"216 S. Rena", name:"216 S. Rena House", unit:"House", tenantRaw:"216 Rena:HSE:Julisa Otzoy-Thompson/Tucker Thompson:Water (Julisa Otzoy-Thompson/Tucker Thompson)", sensorId:146053, counter:4, scale:.001, moveIn:"2023-10-26", moveOut:null, autoInvoice:true, autoEmail:true, invoiceDay:20, minDays:15, autoPay:false, active:true, baseUse:14 }),
    // ---- Siligre / 1923 Teak ----
    U({ managerId:"siligre", templateId:"teakS", propKey:"teak1923", propName:"1923 Teak", name:"1923 Teak A", unit:"A", tenantRaw:"1923 Teak:A:Adan Navarro/Julissa Vega:Water (Adan Navarro/Julissa Vega)", sensorId:372987, counter:1, scale:.001, moveIn:"2024-06-01", moveOut:null, autoInvoice:true, autoEmail:true, invoiceDay:5, minDays:15, autoPay:false, active:true, baseUse:5 }),
    U({ managerId:"siligre", templateId:"teakS", propKey:"teak1923", propName:"1923 Teak", name:"1923 Teak B", unit:"B", tenantRaw:"1923 Teak:B:Martin/Sara Ruiz:Water (Martin/Sara Ruiz)", sensorId:1046246, counter:1, scale:.001, moveIn:"2017-08-01", moveOut:null, autoInvoice:true, autoEmail:true, invoiceDay:5, minDays:15, autoPay:false, active:true, baseUse:7 }),
    U({ managerId:"siligre", templateId:"teakS", propKey:"teak1923", propName:"1923 Teak", name:"1923 Teak C", unit:"C", tenantRaw:"1923 Teak:C:Jose Mendoza:Water (Jose Mendoza)", sensorId:1046247, counter:1, scale:.001, moveIn:"2017-08-01", moveOut:null, autoInvoice:true, autoEmail:true, invoiceDay:5, minDays:15, autoPay:false, active:true, baseUse:4 }),
    // ---- Siligre / 521 4th / 381 Saratoga ----
    U({ managerId:"siligre", templateId:"fourth", propKey:"fourth", propName:"521 4th / 381 Saratoga", name:"521 4th 521A", unit:"521A", tenantRaw:"521 4th/381 Saratoga:521A:Monica Sadler:Water (Monica Sadler)", sensorId:520049, counter:1, scale:.001, moveIn:"2019-09-07", moveOut:null, autoInvoice:true, autoEmail:true, invoiceDay:5, minDays:15, autoPay:true, active:true, baseUse:10 }),
    U({ managerId:"siligre", templateId:"fourth", propKey:"fourth", propName:"521 4th / 381 Saratoga", name:"521 4th 521B", unit:"521B", tenantRaw:"521 4th/381 Saratoga:521B:Suraj Kar/April Sears:Water (Suraj Kar/April Sears)", sensorId:520044, counter:1, scale:.001, moveIn:"2025-08-15", moveOut:null, autoInvoice:true, autoEmail:true, invoiceDay:5, minDays:15, autoPay:false, active:true, baseUse:12 }),
    U({ managerId:"siligre", templateId:"fourth", propKey:"fourth", propName:"521 4th / 381 Saratoga", name:"381 Saratoga 381A", unit:"381A", tenantRaw:"521 4th/381 Saratoga:381A:Stasja Knopf/Daryl Carlin:Water (Stasja Knopf/Daryl Carlin)", sensorId:520042, counter:1, scale:.001, moveIn:"2024-10-01", moveOut:null, autoInvoice:true, autoEmail:true, invoiceDay:5, minDays:15, autoPay:false, active:true, baseUse:9 }),
    U({ managerId:"siligre", templateId:"fourth", propKey:"fourth", propName:"521 4th / 381 Saratoga", name:"381 Saratoga 381B", unit:"381B", tenantRaw:"521 4th/381 Saratoga:381B:Samantha Lopez/Peter Rodriguez:Water (Samantha Lopez/Peter Rodriguez)", sensorId:870897, counter:1, scale:.001, moveIn:"2024-11-08", moveOut:null, autoInvoice:true, autoEmail:true, invoiceDay:5, minDays:15, autoPay:false, active:true, baseUse:13 }),
    // ---- Siligre / 2361 Cienaga ----
    U({ managerId:"siligre", templateId:"cienaga", propKey:"cienaga", propName:"2361 Cienaga", name:"2361 Cienaga B", unit:"B", tenantRaw:"2361 Cienaga:B:Jean Lawley/Louis Callison:Water (Jean Lawley/Louis Callison)", sensorId:789253, counter:1, scale:.001, moveIn:"2023-07-01", moveOut:null, autoInvoice:true, autoEmail:true, invoiceDay:5, minDays:15, autoPay:false, active:true, baseUse:8 }),
    U({ managerId:"siligre", templateId:"cienaga", propKey:"cienaga", propName:"2361 Cienaga", name:"2361 Cienaga C", unit:"C", tenantRaw:"2361 Cienaga:C:Holly Minarik/Derek McFate:Water (Holly Minarik/Derek McFate)", sensorId:783740, counter:1, scale:.001, moveIn:"2021-07-07", moveOut:null, autoInvoice:true, autoEmail:true, invoiceDay:5, minDays:15, autoPay:false, active:true, baseUse:10 }),
    U({ managerId:"siligre", templateId:"cienaga", propKey:"cienaga", propName:"2361 Cienaga", name:"2361 Cienaga D", unit:"D", tenantRaw:"2361 Cienaga:D:Emly Goffe/Keenan Hartig:Water (Emly Goffe/Keenan Hartig)", sensorId:783736, counter:1, scale:.001, moveIn:"2025-02-08", moveOut:null, autoInvoice:true, autoEmail:true, invoiceDay:5, minDays:15, autoPay:false, active:true, baseUse:7 }),
    // ---- Greg / 1925 Teak ----
    U({ managerId:"greg", templateId:"teakG", propKey:"teak1925", propName:"1925 Teak", name:"1925 Teak C", unit:"C", tenantRaw:"1925 Teak:C:Estephany Espinoza/Jose Delgado Gutierrez:Water (Estephany Espinoza/Jose Delgado Gutierrez)", sensorId:372958, counter:1, scale:.001, moveIn:"2023-07-15", moveOut:null, autoInvoice:true, autoEmail:true, invoiceDay:5, minDays:15, autoPay:false, active:true, baseUse:6 }),
    U({ managerId:"greg", templateId:"teakG", propKey:"teak1925", propName:"1925 Teak", name:"1925 Teak D", unit:"D", tenantRaw:"1925 Teak:D:Hermino Perez/Adriana Rivera:Water (Hermino Perez/Adriana Rivera)", sensorId:372962, counter:1, scale:.001, moveIn:"2017-02-01", moveOut:null, autoInvoice:true, autoEmail:true, invoiceDay:5, minDays:15, autoPay:false, active:true, baseUse:9 }),
    U({ managerId:"greg", templateId:"teakG", propKey:"teak1925", propName:"1925 Teak", name:"1925 Teak AB", unit:"AB", tenantRaw:"1925 Teak:AB:NOT IMPLEMENTED", sensorId:372985, counter:1, scale:.001, moveIn:"2017-02-01", moveOut:null, autoInvoice:false, autoEmail:false, invoiceDay:5, minDays:10, autoPay:false, active:false, baseUse:0 }),
    // ---- Guy / 941-65 Manhattan (scale 1/748.052) ----
    U({ managerId:"guy", templateId:"manhattan", propKey:"manhattan", propName:"941-65 Manhattan", name:"941 Manhattan A", unit:"A", tenantRaw:"941 Manhattan:A:Phlora Barbash:Water (Phlora Barbash)", sensorId:1046245, counter:1, scale:1/748.052, moveIn:"2021-11-01", moveOut:null, autoInvoice:true, autoEmail:true, invoiceDay:5, minDays:10, autoPay:false, active:true, baseUse:8 }),
    U({ managerId:"guy", templateId:"manhattan", propKey:"manhattan", propName:"941-65 Manhattan", name:"941 Manhattan B", unit:"B", tenantRaw:"941 Manhattan:B:Brooke Lemke and Ian Eckert-Williams:Water (Brooke Lemke and Ian Eckert-Williams)", sensorId:402734, counter:1, scale:1/748.052, moveIn:"2025-01-18", moveOut:null, autoInvoice:true, autoEmail:true, invoiceDay:5, minDays:10, autoPay:false, active:true, baseUse:11 }),
    U({ managerId:"guy", templateId:"manhattan", propKey:"manhattan", propName:"941-65 Manhattan", name:"941 Manhattan C", unit:"C", tenantRaw:"941 Manhattan:C:Alexandria Applehans:Water (Alexandria Applehans)", sensorId:402737, counter:1, scale:1/748.052, moveIn:"2023-01-13", moveOut:null, autoInvoice:true, autoEmail:true, invoiceDay:5, minDays:10, autoPay:false, active:true, baseUse:7 }),
    U({ managerId:"guy", templateId:"manhattan", propKey:"manhattan", propName:"941-65 Manhattan", name:"941 Manhattan D", unit:"D", tenantRaw:"941 Manhattan:D:Charlotte Helen Burkholder:Water (Charlotte Helen Burkholder)", sensorId:402736, counter:1, scale:1/748.052, moveIn:"2019-01-01", moveOut:null, autoInvoice:true, autoEmail:true, invoiceDay:5, minDays:10, autoPay:false, active:true, baseUse:6 }),
    U({ managerId:"guy", templateId:"manhattan", propKey:"manhattan", propName:"941-65 Manhattan", name:"941 Manhattan E", unit:"E", tenantRaw:"941 Manhattan:E:Margaux Jones and Madison Wethington:Water (Margaux Jones and Madison Wethington)", sensorId:402738, counter:1, scale:1/748.052, moveIn:"2022-01-01", moveOut:null, autoInvoice:true, autoEmail:true, invoiceDay:5, minDays:10, autoPay:false, active:true, baseUse:10 }),
    U({ managerId:"guy", templateId:"manhattan", propKey:"manhattan", propName:"941-65 Manhattan", name:"941 Manhattan G", unit:"G", tenantRaw:"941 Manhattan:G:Abigail Galloway/Jacob Erickson:Water (Abigail Galloway/Jacob Erickson)", sensorId:402739, counter:1, scale:1/748.052, moveIn:"2018-09-16", moveOut:null, autoInvoice:true, autoEmail:true, invoiceDay:5, minDays:10, autoPay:false, active:true, baseUse:12 }),
    U({ managerId:"guy", templateId:"manhattan", propKey:"manhattan", propName:"941-65 Manhattan", name:"941 Manhattan H", unit:"H", tenantRaw:"941 Manhattan:H:Olivia and Kirsten Ross:Water (Olivia and Kirsten Ross)", sensorId:372959, counter:1, scale:1/748.052, moveIn:"2019-07-22", moveOut:null, autoInvoice:true, autoEmail:true, invoiceDay:5, minDays:10, autoPay:true, active:true, baseUse:9 }),
    U({ managerId:"guy", templateId:"manhattan", propKey:"manhattan", propName:"941-65 Manhattan", name:"941 Manhattan I", unit:"I", tenantRaw:"941 Manhattan:I:Whitney Warren:Water (Whitney Warren)", sensorId:372976, counter:1, scale:1/748.052, moveIn:"2024-10-15", moveOut:null, autoInvoice:true, autoEmail:true, invoiceDay:5, minDays:10, autoPay:false, active:true, baseUse:5 }),
    // ---- Sharon / 405-445 7th ----
    U({ managerId:"sharon", templateId:"seventh", propKey:"seventh", propName:"405-445 7th", name:"405-445 7th 435", unit:"435", tenantRaw:"405-445 7th:435:Jessica Manas/Micaela Gonzalez:Water (Jessica Manas/Micaela Gonzalez)", sensorId:727694, counter:1, scale:.001, moveIn:"2022-06-16", moveOut:null, autoInvoice:true, autoEmail:true, invoiceDay:5, minDays:10, autoPay:false, active:true, baseUse:8 }),
    // ---- Lily / 3510 Spring ----
    U({ managerId:"lily", templateId:"spring", propKey:"spring", propName:"3510 Spring", name:"3510 Spring B", unit:"B", tenantRaw:"3510 Spring:B:Julio Penaloza:Water (Julio Penaloza)", sensorId:263092, counter:1, scale:.001, moveIn:"2016-01-01", moveOut:null, autoInvoice:true, autoEmail:true, invoiceDay:5, minDays:15, autoPay:false, active:true, baseUse:7 }),
    U({ managerId:"lily", templateId:"spring", propKey:"spring", propName:"3510 Spring", name:"3510 Spring C", unit:"C", tenantRaw:"3510 Spring:C:Maria Valdez (Rosa Gutierrez):Water (Maria Valdez)", sensorId:783739, counter:1, scale:.001, moveIn:"2016-01-01", moveOut:null, autoInvoice:true, autoEmail:true, invoiceDay:5, minDays:15, autoPay:false, active:true, baseUse:10 }),
    U({ managerId:"lily", templateId:"spring", propKey:"spring", propName:"3510 Spring", name:"3510 Spring D", unit:"D", tenantRaw:"3510 Spring:D:Maria Guizar:Water (Maria Guizar)", sensorId:783738, counter:1, scale:.001, moveIn:"2016-01-01", moveOut:null, autoInvoice:true, autoEmail:true, invoiceDay:5, minDays:15, autoPay:false, active:true, baseUse:6 }),
  ];

  // parse a clean tenant name from the packed QBOTenantName
  function cleanTenant(raw) {
    if (!raw) return "\u2014";
    const parts = raw.split(":");
    let names = parts.length >= 3 ? parts[2] : raw;
    if (/NOT IMPLEMENTED/i.test(names)) return "Vacant";
    names = names.replace(/\s*\(.*?\)\s*/g, " ").trim();
    return names.replace(/\//g, " & ");
  }

  // ----------------------------------------------------------- build units
  let uid = 1000;
  const units = rawUnits.map((r) => {
    const tpl = templates.find((t) => t.id === r.templateId);
    const id = "U" + (uid++);
    return Object.assign({}, r, {
      id,
      tenant: cleanTenant(r.tenantRaw),
      template: tpl,
      moveInDate: new Date(r.moveIn),
      moveOutDate: r.moveOut ? new Date(r.moveOut) : null,
    });
  });

  // -------------------------------------------------- synthesise meter reads
  // Per unit: monthly history of calculated reads, with a running odometer.
  // usage = units consumed in the period (the invoice qty).
  function buildReadings(unit) {
    const rng = seeded("read" + unit.id);
    const reads = [];
    const start = new Date(Math.max(unit.moveInDate.getTime(), new Date(2025, 4, 1).getTime()));
    // start odometer
    let odo = 1200 + Math.floor(rng() * 8000);
    // base read (move-in)
    let cursor = new Date(start.getFullYear(), start.getMonth(), unit.invoiceDay);
    reads.push({ date: new Date(cursor), raw: odo, usage: 0, typeId: 8, readBy: "Base", invoiced: true });
    let m = new Date(cursor);
    m.setMonth(m.getMonth() + 1);
    while (m <= NOW) {
      const season = 1 + 0.28 * Math.sin((m.getMonth() / 12) * Math.PI * 2 - 1);
      let use = Math.max(1, Math.round(unit.baseUse * season * (0.72 + rng() * 0.7)));
      // occasional spike (leak) flag
      const spike = rng() > 0.93;
      if (spike) use = Math.round(use * (2.2 + rng()));
      odo += use;
      const isLatest = false;
      reads.push({
        date: new Date(m),
        raw: odo,
        usage: use,
        typeId: 2,
        readBy: "Automated",
        invoiced: true,
        spike,
      });
      m = new Date(m);
      m.setMonth(m.getMonth() + 1);
    }
    // mark the last read as the current pending (not yet invoiced) period
    if (reads.length > 1 && !unit.moveOutDate) {
      reads[reads.length - 1].invoiced = false;
      reads[reads.length - 1].pending = true;
    }
    return reads;
  }

  units.forEach((u) => { u.reads = u.active ? buildReadings(u) : []; });

  // current reading helpers
  units.forEach((u) => {
    const r = u.reads;
    u.lastRead = r.length ? r[r.length - 1] : null;
    u.priorInvoiced = null;
    for (let i = r.length - 1; i >= 0; i--) { if (r[i].invoiced) { u.priorInvoiced = r[i]; break; } }
    u.pendingRead = r.length && r[r.length - 1].pending ? r[r.length - 1] : null;
    u.pendingUsage = u.pendingRead ? u.pendingRead.usage : 0;
  });

  // ------------------------------------------------- invoice computation
  // Mirrors WaterInvoice.submitInvoice: fixed lines billed per month (qty=months),
  // volume line qty = usage. Returns {lines, total}.
  function computeInvoice(unit, usage, months) {
    months = months == null ? 1 : months;
    const lines = unit.template.lines.slice().sort((a, b) => a.order - b.order).map((ld) => {
      const qty = ld.fixed ? months : usage;
      const amount = Math.round(qty * ld.price * 100) / 100;
      return { desc: ld.desc, fixed: ld.fixed, qty, rate: ld.price, amount };
    });
    const total = Math.round(lines.reduce((s, l) => s + l.amount, 0) * 100) / 100;
    return { lines, total };
  }

  // ------------------------------------------------- synthesise invoices
  let invNo = 3840;
  const invoices = [];
  units.forEach((u) => {
    if (!u.active) return;
    const rng = seeded("inv" + u.id);
    // every invoiced calc read (skip the base) becomes a past invoice
    for (let i = 1; i < u.reads.length; i++) {
      const r = u.reads[i];
      if (!r.invoiced) continue;
      const prev = u.reads[i - 1];
      const comp = computeInvoice(u, r.usage, 1);
      const dueDate = new Date(r.date); dueDate.setDate(u.template.invoiceDay + 15);
      const ageDays = (NOW - r.date) / 86400000;
      let status;
      if (u.autoPay) status = ageDays > 6 ? "Paid" : "Sent";
      else {
        const roll = rng();
        if (ageDays > 50) status = roll > 0.08 ? "Paid" : "Overdue";
        else if (ageDays > 20) status = roll > 0.3 ? "Paid" : (roll > 0.12 ? "Sent" : "Overdue");
        else status = roll > 0.55 ? "Paid" : "Sent";
      }
      invoices.push({
        id: "INV" + (invNo),
        number: String(invNo++),
        unitId: u.id,
        managerId: u.managerId,
        date: new Date(r.date),
        dueDate,
        periodStart: new Date(prev.date),
        periodEnd: new Date(r.date),
        startRead: prev.raw,
        endRead: r.raw,
        usage: r.usage,
        lines: comp.lines,
        total: comp.total,
        status,
        autoPay: u.autoPay,
        emailed: u.autoEmail,
        spike: r.spike,
      });
    }
  });
  invoices.sort((a, b) => b.date - a.date);

  // ------------------------------------------------- sensors (APISensor)
  const rng = seeded("sensors");
  const sensorMap = {};
  units.forEach((u) => {
    if (!sensorMap[u.sensorId]) {
      sensorMap[u.sensorId] = {
        sensorId: u.sensorId,
        name: u.propName + " Meter",
        counters: [],
        unitIds: [],
        scale: u.scale,
      };
    }
    sensorMap[u.sensorId].counters.push(u.counter);
    sensorMap[u.sensorId].unitIds.push(u.id);
  });
  const sensors = Object.values(sensorMap).map((s, i) => {
    const r = seeded("sens" + s.sensorId);
    const battery = Math.round(28 + r() * 70);
    const signal = Math.round(30 + r() * 70);   // 0-100 signal-quality %
    let hoursAgo = r() * 14;
    // a couple of stragglers communicate late
    if (s.sensorId === 402737) hoursAgo = 79; // Manhattan C late
    if (s.sensorId === 783740) hoursAgo = 52; // Cienaga C late
    const lastComm = addHours(NOW, -hoursAgo);
    const nextComm = addHours(lastComm, 8);
    const lowBatt = battery < 35 || s.sensorId === 372976;
    const offline = hoursAgo > 48;
    const liveUnit = units.find((u) => u.id === s.unitIds[0]);
    const curReading = liveUnit && liveUnit.lastRead ? liveUnit.lastRead.raw : Math.round(2000 + r() * 8000);
    return {
      sensorId: s.sensorId,
      name: s.name,
      counters: s.counters,
      unitIds: s.unitIds,
      battery: s.sensorId === 372976 ? 19 : battery,
      signal,
      lastComm,
      nextComm,
      currentReading: curReading,
      alertsActive: lowBatt || offline,
      lowBatt,
      offline,
      monnitAppId: 9351 + i,
      status: offline ? 0 : 1,
    };
  });

  // ------------------------------------------------- properties (grouped)
  const propMap = {};
  units.forEach((u) => {
    if (!propMap[u.propKey]) {
      propMap[u.propKey] = { key: u.propKey, name: u.propName, managerId: u.managerId, templateId: u.templateId, unitIds: [] };
    }
    propMap[u.propKey].unitIds.push(u.id);
  });
  const properties = Object.values(propMap);

  // ------------------------------------------------- activity log (Log table)
  const logSeed = seeded("log");
  const logTypes = [
    { src: "Monnit", msg: (d) => `SyncDataWithMonnit completed \u2014 ${d} sensors, ${Math.round(20 + logSeed() * 60)} messages imported` },
    { src: "QBO", msg: () => `Invoice created for ${pick(units).tenant}` },
    { src: "QBO", msg: () => `Invoice emailed to ${pick(units).tenant}` },
  ];
  function pick(arr) { return arr[Math.floor(logSeed() * arr.length)]; }
  const activity = [];
  for (let i = 0; i < 26; i++) {
    const t = logTypes[Math.floor(logSeed() * logTypes.length)];
    activity.push({
      id: "L" + i,
      source: t.src,
      message: t.src === "Monnit" ? t.msg(sensors.length) : t.msg(),
      level: logSeed() > 0.9 ? "Error" : "Info",
      time: addHours(NOW, -i * (3 + logSeed() * 9)),
    });
  }
  // a few explicit notable events
  activity.unshift({ id: "Lx0", source: "Monnit", message: "Sensor 372976 (941 Manhattan I) battery low \u2014 19%", level: "Warning", time: addHours(NOW, -2.1) });
  activity.unshift({ id: "Lx1", source: "QBO", message: "Token refresh failed for Colpitts Properties \u2014 reconnect required", level: "Error", time: addHours(NOW, -5.0) });
  activity.sort((a, b) => b.time - a.time);

  // ------------------------------------------------- date helpers
  function addDays(d, n) { const r = new Date(d); r.setTime(r.getTime() + n * 86400000); return r; }
  function addHours(d, n) { const r = new Date(d); r.setTime(r.getTime() + n * 3600000); return r; }

  // ------------------------------------------------- billing-run eligibility
  // Mirrors CreateInvoice.CreateInvoices logic for a chosen read date.
  function billRunPreview(managerId, readDate) {
    const list = units.filter((u) => u.active && u.autoInvoice && (managerId === "all" || u.managerId === managerId));
    return list.map((u) => {
      const prior = u.priorInvoiced;
      const pending = u.pendingRead;
      const usage = pending ? pending.usage : 0;
      const periodDays = prior && pending ? Math.round((pending.date - prior.date) / 86400000) : 0;
      const comp = computeInvoice(u, usage, 1);
      let skip = null;
      if (!pending) skip = "No new read this period";
      else if (periodDays < u.minDays) skip = `Only ${periodDays} days since last bill (min ${u.minDays})`;
      else if (u.invoiceDay > NOW.getDate()) skip = `Invoice day is the ${ordinal(u.invoiceDay)} \u2014 not reached`;
      const lowBattSensor = sensors.find((s) => s.sensorId === u.sensorId && s.offline);
      return {
        unit: u, prior, pending, usage, periodDays, comp,
        eligible: !skip, skip,
        warnStale: !!lowBattSensor,
        finalInvoice: false,
        readDateOverride: null,
      };
    });
  }
  function ordinal(n) {
    const s = ["th", "st", "nd", "rd"], v = n % 100;
    return n + (s[(v - 20) % 10] || s[v] || s[0]);
  }

  // ------------------------------------------------- expose
  window.DATA = {
    NOW,
    managers,
    templates,
    units,
    properties,
    invoices,
    sensors,
    activity,
    meterReadTypes,
    unitTypes,
    computeInvoice,
    billRunPreview,
    cleanTenant,
    ordinal,
    addDays,
    addHours,
    // lookups
    manager: (id) => managers.find((m) => m.id === id),
    unit: (id) => units.find((u) => u.id === id),
    template: (id) => templates.find((t) => t.id === id),
    sensor: (id) => sensors.find((s) => s.sensorId === id),
    unitsFor: (managerId) => managerId === "all" ? units : units.filter((u) => u.managerId === managerId),
    invoicesFor: (managerId) => managerId === "all" ? invoices : invoices.filter((i) => i.managerId === managerId),
    propertiesFor: (managerId) => managerId === "all" ? properties : properties.filter((p) => p.managerId === managerId),
    templatesFor: (managerId) => managerId === "all" ? templates : templates.filter((t) => t.managerId === managerId),
    sensorsFor: (managerId) => {
      if (managerId === "all") return sensors;
      const ids = new Set(units.filter((u) => u.managerId === managerId).map((u) => u.sensorId));
      return sensors.filter((s) => ids.has(s.sensorId));
    },
  };
})();

// ESM export for the Vite app (keeps the window.DATA global harmlessly intact)
export const DATA = window.DATA;
